import * as React from 'react';

import { DescriptionPage_ProductVariant as DescriptionPageProductVariant } from '__generated__/graphql-types';
import type {
  DescriptionPage_Course as DescriptionPageCourse,
  DescriptionPage_Partner as DescriptionPagePartner,
  DescriptionPage_SpecializationPage as DescriptionPageSpecializationPage,
} from '__generated__/graphql-types-do-not-use';

import {
  XDP_META_DESC_LENGTH_LIMIT,
  getXdpMetaDescriptionSuffix,
} from 'bundles/enterprise-legacy-learner-home/constants/xdpConstants';
import type { Course, Domain, Partner } from 'bundles/enterprise-legacy-learner-home/types/xdpTypes';
import { getSEODescription } from 'bundles/enterprise-legacy-learner-home/utils/xdpUtils';
import { toAggregateRating, toEducationLevelValue } from 'bundles/seo/utils/courseInfoSchemaMarkupUtils';
import {
  KEYWORD_IDS,
  getBreadcrumbSchema,
  getProviderMarkup,
  getSchemaMarkupElement,
  getSchemaOrgBooleanString,
  getTrimmedDescription,
  monthsToISO8601Duration,
} from 'bundles/seo/utils/schemaMarkupUtils';

type Props = {
  domain?: Domain;
  slug?: string;
  name: string;
  skills?: Array<string> | null;
  description: string;
  partners?: Array<Partner | DescriptionPagePartner> | null;
  logo?: string;
  primaryLanguage: string;
  timeRequiredInMonths?: number | null;
  courses?: Array<Course | DescriptionPageCourse> | null;
  enableCoBranding?: boolean;
  productVariant?: (typeof DescriptionPageProductVariant)[keyof typeof DescriptionPageProductVariant];
  ratings: DescriptionPageSpecializationPage['ratings'];
  difficultyLevel: DescriptionPageSpecializationPage['difficultyLevel'];
};

const S12nXDPSchemaMarkup = ({
  domain,
  slug,
  name,
  skills,
  description,
  partners,
  logo,
  primaryLanguage,
  timeRequiredInMonths,
  courses,
  enableCoBranding,
  productVariant,
  ratings,
  difficultyLevel,
}: Props) => {
  const breadcrumbType = getBreadcrumbSchema(domain, KEYWORD_IDS.SPECIALIZATION);
  const descriptionWithLengthLimit = `${getTrimmedDescription(
    description,
    XDP_META_DESC_LENGTH_LIMIT - getXdpMetaDescriptionSuffix().length
  )} ${getXdpMetaDescriptionSuffix()}`;
  const partner = partners?.[0];
  const courseType = courses?.map((course) => ({
    '@id': `https://www.coursera.org/learn/${course.slug}#${KEYWORD_IDS.COURSE}`,
    '@type': 'Course',
    name: course.name,
    description: `${getTrimmedDescription(
      getSEODescription(course.description || '', partners, enableCoBranding),
      XDP_META_DESC_LENGTH_LIMIT - getXdpMetaDescriptionSuffix().length
    )} ${getXdpMetaDescriptionSuffix()}`,
    provider: partner ? getProviderMarkup([partner]) : undefined,
  }));
  const estimatedTimeRequiredForCompletionInMonths =
    typeof timeRequiredInMonths === 'number' ? monthsToISO8601Duration(timeRequiredInMonths) : undefined;

  const productPath =
    productVariant === DescriptionPageProductVariant.ProfessionalCertificate
      ? 'professional-certificates'
      : 'specializations';

  const specializationType = {
    '@id': `https://www.coursera.org/${productPath}/${slug}#${KEYWORD_IDS.SPECIALIZATION}`,
    '@type': 'Course',
    name,
    url: `https://www.coursera.org/${productPath}/${slug}`,
    About: {
      '@type': 'Thing',
      name: skills ? [...skills] : undefined,
      subjectOf: name,
    },
    description: descriptionWithLengthLimit,
    provider: partner ? getProviderMarkup([partner]) : undefined,
    image: logo,
    offers: {
      '@type': 'Offer',
      category: 'Subscription',
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: 'Online',
      courseWorkload: estimatedTimeRequiredForCompletionInMonths,
    },
    educationalCredentialAwarded: 'Coursera Certificate',
    isAccessibleForFree: getSchemaOrgBooleanString(true),
    inLanguage: primaryLanguage,
    timeRequired: estimatedTimeRequiredForCompletionInMonths,
    hasPart: courseType,
    educationalLevel: toEducationLevelValue(difficultyLevel),
    ...toAggregateRating(ratings),
  };

  const graph = [breadcrumbType, specializationType];

  return <div className="rc-S12nXDPSchemaMarkup">{getSchemaMarkupElement(graph)}</div>;
};

export default S12nXDPSchemaMarkup;
